import { useMemo } from 'react';

import type { SelectionList } from '@revelio/iso-utility';

import {
  buildFieldGranularityMap,
  buildFieldLabelMap,
  type ColumnOption,
} from '../../api/columns-api-helpers';
import type { DataBuilderModel } from '../../api/data-builder-api.types';
import { useDataBuilderQuery } from '../../api/useDataBuilderQuery';
import { resolveSelectionListItemLabel } from '../../components/custom-filters/data-builder-selection-list-mapping';
import { useDataBuilderSelectionLists } from '../../components/custom-filters/useDataBuilderSelectionLists';
import { buildCompaniesData } from '../../components/ui/CompaniesSummary';
import type {
  DatasetSummaryColumn,
  DatasetSummaryData,
  DatasetSummarySetting,
} from '../../components/ui/DatasetSummaryCard';
import type { ModelVersionEntry } from '../../components/ui/ModelVersionsSummary';
import {
  type DeliveryFormat,
  type DeliveryMethod,
  type S3DeliveryConfig,
  type SnowflakeDeliveryConfig,
  selectConfigByInstance,
  selectDatasetInstances,
  selectDeliveryFormat,
  selectDeliveryMethod,
  selectModelVersions,
  selectS3Config,
  selectSnowflakeConfig,
  useDataBuilderSelector,
} from '../../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../../datasets/dataset-instance-labels';
import type { DeliverableColumnConfig } from '../../datasets/deliverable-column-config';
import {
  getVersionDisplayLabel,
  isLatestVersions,
} from '../step2-column/model-utils';

type UseConfirmationSummaryResult = {
  datasetSummaries: DatasetSummaryData[];
  selectionLists: SelectionList[];
  deliveryMethod: DeliveryMethod;
  deliveryFormat: DeliveryFormat;
  snowflakeConfig: SnowflakeDeliveryConfig | null;
  s3Config: S3DeliveryConfig | null;
  lagData: number;
};

const AGGREGATION_BREAKDOWN_ID = 'aggregation';

/**
 * Resolve selected column keys to labels in API order (matching Step 2 UI).
 */
const resolveColumns = (
  selectedKeys: string[],
  columnOptions: ColumnOption[]
): DatasetSummaryColumn[] => {
  const selectedSet = new Set(selectedKeys);
  // Filter API-ordered options to only those selected, preserving API order
  return columnOptions
    .filter((opt) => selectedSet.has(opt.key))
    .map((opt) => ({ key: opt.key, label: opt.label }));
};

/**
 * Resolve settings to display labels using API definitions.
 */
const resolveSettings = (
  storeSettings: Record<string, unknown>,
  config: DeliverableColumnConfig | undefined
): DatasetSummarySetting[] => {
  const entries = Object.entries(storeSettings);
  if (entries.length === 0 || !config) {
    return entries.map(([k, v]) => ({ label: k, displayValue: String(v) }));
  }

  return entries.map(([key, value]) => {
    const settingDef = config.settings.find((s) => s.key === key);

    const label = settingDef && 'name' in settingDef ? settingDef.name : key;

    let displayValue: string;
    if (typeof value === 'string' && settingDef && 'items' in settingDef) {
      const item = settingDef.items.find(
        (i: { value: string; name: string }) => i.value === value
      );
      displayValue = item ? item.name : value;
    } else if (typeof value === 'boolean') {
      displayValue = value ? 'On' : 'Off';
    } else {
      displayValue = String(value);
    }

    return { label, displayValue };
  });
};

/**
 * Resolve unit of analysis value to its API label.
 */
const resolveUnitOfAnalysisLabel = (
  value: string | null,
  config: DeliverableColumnConfig | undefined
): string => {
  if (!value || value === 'default') {
    return 'default';
  }
  const option = config?.unitOfAnalysis.options.find(
    (opt) => opt.value === value
  );
  return option?.label ?? value;
};

/**
 * Resolve model version entries to display labels matching the Step 2 dropdown.
 */
const resolveModelVersionEntries = (
  modelVersions: Record<string, unknown>,
  models: DataBuilderModel[]
): ModelVersionEntry[] => {
  return Object.entries(modelVersions).map(([key, value]) => {
    const model = models.find((m) => m.key === key);
    const modelLabel = model?.name ?? key;
    const versionLabel =
      model && (typeof value === 'number' || typeof value === 'string')
        ? getVersionDisplayLabel(model, value)
        : String(value);
    return { modelLabel, versionLabel };
  });
};

export const useConfirmationSummary = (): UseConfirmationSummaryResult => {
  const { data } = useDataBuilderQuery();
  const instances = useDataBuilderSelector(selectDatasetInstances);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const deliveryMethod = useDataBuilderSelector(selectDeliveryMethod);
  const deliveryFormat = useDataBuilderSelector(selectDeliveryFormat);
  const snowflakeConfig = useDataBuilderSelector(selectSnowflakeConfig);
  const s3Config = useDataBuilderSelector(selectS3Config);
  const lagData = useDataBuilderSelector((state) => state.lagData);
  const modelVersions = useDataBuilderSelector(selectModelVersions);

  // Collect all selection list keys from filters/custom columns across all instances
  // so the confirmation step can resolve ID-only labels for datasets whose
  // filters were never visited on the Columns & Filters step.
  const allSelectionListKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const config of Object.values(configByInstance)) {
      const allFilters = [
        ...(config?.customFilters ?? []),
        ...Object.values(config?.customFiltersByBreakdown ?? {}).flat(),
      ];
      const allColumns = Object.values(
        config?.customColumnsByBreakdown ?? {}
      ).flat();

      for (const item of config?.unitOfAnalysisSelections ?? []) {
        if (item.selectionListId) keys.add(item.selectionListId);
      }
      for (const filter of allFilters) {
        for (const c of filter.conditions) {
          if (c.type === 'selection_list' && c.selectionListItems) {
            for (const item of c.selectionListItems) {
              if (item.selectionListId) keys.add(item.selectionListId);
            }
          }
        }
      }
      for (const col of allColumns) {
        for (const step of col.steps) {
          if (!step.case) continue;
          for (const c of step.case.conditions) {
            if (c.type === 'selection_list' && c.selectionListItems) {
              for (const item of c.selectionListItems) {
                if (item.selectionListId) keys.add(item.selectionListId);
              }
            }
          }
        }
      }
    }
    return Array.from(keys);
  }, [configByInstance]);

  const { selectionLists } = useDataBuilderSelectionLists({
    apiKeys: allSelectionListKeys,
  });

  const deliverablesConfig = data?.deliverablesConfig ?? {};
  const labelMap = buildDatasetLabelMap(data?.datasets);
  const instanceLabelMap = buildDatasetInstanceLabelMap(instances, labelMap);
  const models: DataBuilderModel[] = data?.models ?? [];
  const resolvedModelEntries = resolveModelVersionEntries(
    modelVersions,
    models
  );
  const allLatest = isLatestVersions(modelVersions, models);

  const datasetSummaries: DatasetSummaryData[] = instances.map((instance) => {
    const label = instanceLabelMap[instance.instanceId] ?? instance.datasetKey;
    const config = configByInstance[instance.instanceId];
    const deliverableConfig = deliverablesConfig[instance.datasetKey];
    const companyEntries = Object.entries(config?.companySelections ?? {});
    const allColumnEntries = Object.entries(
      config?.columnKeysByBreakdown ?? {}
    );
    // If at least one breakdown has selections, hide breakdowns with empty
    // selections so the confirmation doesn't render rows for unselected files.
    // If ALL breakdowns are empty, keep them so DatasetSummaryCard's
    // "Default" fallback (allColumns.length === 0) still triggers.
    const hasAnySelections = allColumnEntries.some(
      ([, keys]) => keys.length > 0
    );
    const columnEntries = hasAnySelections
      ? allColumnEntries.filter(([, keys]) => keys.length > 0)
      : allColumnEntries;
    const topLevelFilters = config?.customFilters ?? [];
    const breakdownFilterEntries = Object.entries(
      config?.customFiltersByBreakdown ?? {}
    );
    const customColumnEntries = Object.entries(
      config?.customColumnsByBreakdown ?? {}
    );

    const resolveBreakdownLabel = (breakdownId: string): string => {
      if (breakdownId === AGGREGATION_BREAKDOWN_ID) return 'Aggregation';
      const breakdown = deliverableConfig?.breakdowns.find(
        (b) => b.id === breakdownId
      );
      return breakdown?.label ?? breakdownId;
    };

    // Build a unified field label map from ALL filter definitions
    // (general + all breakdowns) so labels resolve regardless of where
    // the filter was created.
    const allFilterDefs = [
      ...(deliverableConfig?.generalFilters.customFilterDefinitions ?? []),
      ...(deliverableConfig?.breakdowns.flatMap(
        (b) => b.customFilterDefinitions
      ) ?? []),
      ...(deliverableConfig?.breakdowns.flatMap(
        (b) => b.customColumnFilterDefinitions
      ) ?? []),
    ];
    const unifiedFieldLabelMap = buildFieldLabelMap(allFilterDefs);
    const unifiedFieldGranularityMap = buildFieldGranularityMap(allFilterDefs);

    // Resolve columns with API labels in API order, aggregation first
    const resolvedColumns = columnEntries
      .map(([breakdownId, keys]) => {
        let columnOptions: ColumnOption[];
        let breakdownLabel: string;
        if (breakdownId === AGGREGATION_BREAKDOWN_ID) {
          columnOptions = deliverableConfig?.aggregationColumns.options ?? [];
          breakdownLabel = 'Aggregation';
        } else {
          const breakdown = deliverableConfig?.breakdowns.find(
            (b) => b.id === breakdownId
          );
          columnOptions = breakdown?.columnOptions ?? [];
          breakdownLabel = breakdown?.label ?? breakdownId;
        }
        return {
          breakdownId,
          breakdownLabel,
          columns: resolveColumns(keys, columnOptions),
        };
      })
      .sort((a, b) => {
        if (a.breakdownId === AGGREGATION_BREAKDOWN_ID) return -1;
        if (b.breakdownId === AGGREGATION_BREAKDOWN_ID) return 1;
        return 0;
      });

    // Collect included breakdown (file) labels as granularities
    const granularities = columnEntries
      .filter(([id]) => id !== AGGREGATION_BREAKDOWN_ID)
      .map(([id]) => {
        const breakdown = deliverableConfig?.breakdowns.find(
          (b) => b.id === id
        );
        return breakdown?.label ?? id;
      });
    const pipelineInputTable = config?.companyMapper?.pipeline_input || null;

    return {
      instanceId: instance.instanceId,
      datasetKey: instance.datasetKey,
      label,
      unitOfAnalysis: resolveUnitOfAnalysisLabel(
        config?.unitOfAnalysis ?? null,
        deliverableConfig
      ),
      unitOfAnalysisSelections: (config?.unitOfAnalysisSelections ?? []).map(
        (item) => {
          if (item.label !== item.id) return item;
          const resolved = resolveSelectionListItemLabel(
            item.id,
            item.selectionListId,
            selectionLists
          );
          return resolved ? { ...item, label: resolved } : item;
        }
      ),
      companies: buildCompaniesData(
        config?.companyMapper,
        config?.companySets,
        companyEntries
      ),
      pipelineInputTable,
      granularities,
      columns: resolvedColumns,
      filterGroups: [
        ...(topLevelFilters.length > 0
          ? [
              {
                breakdownLabel: null,
                filters: topLevelFilters,
                fieldLabelMap: unifiedFieldLabelMap,
                fieldGranularityMap: unifiedFieldGranularityMap,
              },
            ]
          : []),
        ...breakdownFilterEntries
          .filter(([, filters]) => filters.length > 0)
          .map(([breakdownId, filters]) => ({
            breakdownLabel: resolveBreakdownLabel(breakdownId),
            filters,
            fieldLabelMap: unifiedFieldLabelMap,
            fieldGranularityMap: unifiedFieldGranularityMap,
          })),
      ],
      customColumnGroups: customColumnEntries
        .filter(([, columns]) => columns.length > 0)
        .map(([breakdownId, columns]) => ({
          breakdownLabel: resolveBreakdownLabel(breakdownId),
          customColumns: columns,
          fieldLabelMap: unifiedFieldLabelMap,
          fieldGranularityMap: unifiedFieldGranularityMap,
        })),
      settings: resolveSettings(config?.settings ?? {}, deliverableConfig),
      modelVersions: resolvedModelEntries,
      allLatestModelVersions: allLatest,
    };
  });

  return {
    datasetSummaries,
    selectionLists,
    deliveryMethod,
    deliveryFormat,
    snowflakeConfig,
    s3Config,
    lagData,
  };
};
